<script setup lang="ts">
import { computed, ref, shallowRef, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAuthStore } from '@/entities/auth';
import {
  createManualDataRefreshRunner,
  isMobileDataRefreshEnabled,
  PullToRefreshIndicator,
  useDataRefreshCoordinator,
  useMobileRefreshOverscroll,
  usePullToRefresh,
} from '@/features/data-refresh';
import { apolloOperationGate } from '@/shared/api';

import { useSidebar } from '../model/use-sidebar';
import AppHeader from './AppHeader.vue';
import AppSidebar from './AppSidebar.vue';

interface Props {
  minWidth?: string;
  constrainHeight?: boolean;
  headerOverlayPx?: number;
  /**
   * Скрывать sidebar-header на десктопе (рендерить его только на mobile,
   * где он держит бургер). Используется на плотных страницах вроде /messenger,
   * где десктопный header — пустое мёртвое пространство.
   */
  headerMobileOnly?: boolean;
  /**
   * Класс ширины содержимого шапки (`max-w-*`). Страница с центрированной колонкой
   * передаёт ту же ширину, что у колонки: заголовок и действия встают по её краям,
   * а не по краям экрана.
   */
  headerContentClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  minWidth: '0',
  constrainHeight: false,
  headerOverlayPx: 0,
  headerMobileOnly: false,
  headerContentClass: '',
});

const route = useRoute();
const showSidebar = computed(() => route.meta.sidebar !== false);
const { isMobile, sidebarOffset, closeMobileMenu, openMobileMenu } = useSidebar();
const authStore = useAuthStore();
const dataRefreshCoordinator = useDataRefreshCoordinator();
const layoutRoot = ref<HTMLElement | null>(null);
const isManualRefreshActive = shallowRef(false);
const isRefreshGestureEnabled = (): boolean =>
  isMobileDataRefreshEnabled({
    isMobile: isMobile.value,
    isAuthenticated: authStore.isAuthenticated,
    isContextChecked: authStore.isContextChecked,
    hasOrganization: authStore.currentOrganization !== null,
    isRefreshing: dataRefreshCoordinator.status.value === 'refreshing' || isManualRefreshActive.value,
    isTenantTransitioning: apolloOperationGate.isRunning('tenant-transition'),
  });
const manualRefreshRunner = createManualDataRefreshRunner({
  refresh: () => dataRefreshCoordinator.refresh('manual'),
  onActiveChange: (isActive) => {
    isManualRefreshActive.value = isActive;
  },
});
const { phase: refreshGesturePhase, pullDistance: refreshPullDistance } = usePullToRefresh({
  target: layoutRoot,
  enabled: isRefreshGestureEnabled,
  onRefresh: manualRefreshRunner.run,
});

useMobileRefreshOverscroll(isMobile);

/**
 * Вычисляемый margin-left для контента в зависимости от состояния sidebar
 */
const sidebarMargin = computed(() => {
  if (!showSidebar.value || isMobile.value) return '0px';
  return sidebarOffset.value;
});

// margin-left — inline, а не `v-bind` в CSS: Vue ставит такие переменные после mounted детей, и если
// ребёнок при монтировании читает layout, отступ успевает посчитаться нулём и контент «выезжает» слева.
const containerStyles = computed(() => ({
  minWidth: props.minWidth,
  marginLeft: sidebarMargin.value,
}));

/**
 * Верхний отступ контента. На десктопе со скрытым header (`header-mobile-only`)
 * убираем `pt-4`, чтобы верх контента совпадал с верхом сайдбара (тот — `fixed;
 * top: 0`). В остальных случаях (есть header или mobile) сохраняем `pt-4`.
 */
const contentTopPaddingClass = computed(() =>
  showSidebar.value && props.headerMobileOnly && !isMobile.value ? 'pt-0' : 'pt-4',
);

/**
 * Автозакрытие мобильной шторки при смене route
 */
watch(() => route.path, closeMobileMenu);

/**
 * Обработчик клика по кнопке-бургеру
 */
const handleBurgerClick = () => {
  openMobileMenu();
};
</script>

<template>
  <div
    ref="layoutRoot"
    :class="['flex overflow-x-hidden bg-(--surface-page)', props.constrainHeight ? 'h-dvh' : 'min-h-dvh']"
  >
    <PullToRefreshIndicator
      v-if="isMobile"
      :phase="refreshGesturePhase"
      :pull-distance="refreshPullDistance"
      :is-refreshing="isManualRefreshActive"
    />

    <!-- Sidebar слева -->
    <AppSidebar v-if="showSidebar" />

    <!-- Основной контент справа -->
    <div
      :class="[
        'main-content',
        'relative flex min-h-0 flex-1 shrink-0 flex-col gap-3 overflow-hidden px-4',
        contentTopPaddingClass,
      ]"
      :style="containerStyles"
    >
      <!-- Header с sidebar: на десктопе скрывается при header-mobile-only -->
      <template v-if="showSidebar">
        <AppHeader
          v-if="!headerMobileOnly || isMobile"
          :show-burger-menu="isMobile"
          :reserved-right="headerOverlayPx"
          :content-class="headerContentClass"
          @burger-click="handleBurgerClick"
        >
          <template #page-title>
            <slot name="page-title">
              <slot name="navigation" />
            </slot>
          </template>
          <template #header-actions>
            <slot name="header-actions" />
          </template>
        </AppHeader>
      </template>

      <!-- Header без sidebar (auth) -->
      <AppHeader
        v-else
        :show-user-menu="true"
        :content-class="headerContentClass"
      >
        <template #logo>
          <slot name="logo" />
        </template>
        <template #navigation>
          <slot name="navigation" />
        </template>
        <template #header-actions>
          <slot name="header-actions" />
        </template>
      </AppHeader>

      <slot />
      <slot name="overlay" />
    </div>
  </div>
</template>

<style scoped>
.main-content {
  /* Контент едет вместе с панелью подразделов — одна кривая на оба движения. */
  transition: margin-left var(--sidebar-panel-duration) var(--sidebar-panel-easing);
}

@media (max-width: 1279px) {
  .main-content {
    --main-inset-top: max(1rem, env(safe-area-inset-top, 0px));
    --main-inset-right: max(1rem, env(safe-area-inset-right, 0px));
    --main-inset-left: max(1rem, env(safe-area-inset-left, 0px));
    padding-top: var(--main-inset-top);
    padding-right: var(--main-inset-right);
    padding-left: var(--main-inset-left);
  }
}
</style>
